import { ColDef, GridApi, ValueFormatterParams } from "ag-grid-community";
import { Col, Row } from "antd";
import AgGridModalLayout from "@components/wrappers/layouts/AgGridModalLayout";
import useGetModulesData from "@hooks/useGetModulesData";
import { customFilterOptions } from "@ts/constants";
import { isPropPresent, numericValueFormatter, titleCase } from "@ts/utils";
import {
    ICustomCellRendererParams,
    IStatusBadgeCellRenderer,
    DeepDiveGridType,
    TCommonRowData,
    ParentModule,
    SubGrids,
    SubModule,
} from "@ts/common-types";
import {
    ProgramTypeCellRenderer,
    FilterTextFormatter,
    UpdateBidOrBudgetCellRenderer,
    StatusCellRenderer,
    StatusBadgeCellRenderer,
    ChangeInValueCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import WidgetCard from "./WidgetCard";

interface IProductCampaignsWidget {
    title?: string;
    rowData: TCommonRowData;
    setRowData: React.Dispatch<React.SetStateAction<TCommonRowData>>;
    gridApi: GridApi;
    setGridApi: React.Dispatch<React.SetStateAction<GridApi>>;
}
const ProductKeywordsWidget: React.FC<IProductCampaignsWidget> = ({
    title,
    rowData,
    setRowData,
    gridApi,
    setGridApi,
}) => {
    
    const gridParams = useGetModulesData({
        type: "grid-params",
        subGridType: SubModule.KEYWORD,
        activeModuleName: ParentModule.PRODUCT_ANALYTICS,
        activeParentModuleName: ParentModule.PRODUCT_ANALYTICS,
    });

    const productsModalColDefs: ColDef[] = [
        {
            field: "keyword_name",
            headerName: "Target",
            minWidth: 200,
            filter: "agTextColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["text"],
            },
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            tooltipField: "keyword_name",
            cellRenderer: (params: IStatusBadgeCellRenderer) =>
                StatusBadgeCellRenderer(params, DeepDiveGridType.Keyword),
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            field: "bid",
            headerName: "Bid",
            pinned: "left",
            minWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].minWidth
                : 150,
            maxWidth: isPropPresent(gridParams, "bid")
                ? gridParams["bid"].maxWidth
                : 170,
            lockPinned: true,
            filter: false,
            //@ts-ignore
            tooltipValueGetter: ({
                data: { sys_ams_keyword_id, bid },
            }: {
                data: {
                    sys_ams_keyword_id: string;
                    bid: number;
                };
            }) => {
                if (sys_ams_keyword_id === "00000000000000")
                    return `${bid} !operation unavailable`;
                else return bid;
            },
            cellClass: "lock-pinned",
            cellRenderer: (params: ICustomCellRendererParams) =>
                isPropPresent(gridParams, "bid")
                    ? gridParams["bid"].cellRenderer(params, DeepDiveGridType.Keyword)
                    : UpdateBidOrBudgetCellRenderer(params, DeepDiveGridType.Keyword),
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "state",
            headerName: "Bid Status",
            pinned: "left",
            maxWidth: 100,
            lockPinned: true,
            cellRenderer: (params: ICustomCellRendererParams) =>
                StatusCellRenderer(params, DeepDiveGridType.Keyword),
            cellClass: "lock-pinned",
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isModalComponent: true,
            },
        },
        {
            field: "kwd_class",
            headerName: "Keyword Class",
            filter: "agSetColumnFilter",
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? titleCase(params.value) : params.value,
            filterParams: {
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : params.value,
            },
        },
        {
            field: "percent_spend_in_adgroup",
            headerName: "% Spend",
            maxWidth: 150,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRenderer: ChangeInValueCellRenderer,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            field: "rank",
            headerName: "Organic Rank",
            maxWidth: 150,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: false,
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            filter: "agTextColumnFilter",
            cellRenderer: (params: IStatusBadgeCellRenderer) =>
                StatusBadgeCellRenderer(params, DeepDiveGridType.Keyword),
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
            },
        },
        {
            headerName: "Impressions",
            field: "impression",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: gridParams["click"]?.headerName || "Clicks",
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: "Spends",
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Orders",
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
                hasPinnedColumnValue: true,
            },
            hide: true,
        },
        {
            headerName: "Sales",
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "Total Ad Sales",
            cellStyle: { whiteSpace: "normal" },
            field: "total_clicked_ad_sales",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            field: "troas",
            headerName: "TRoAS",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CTR",
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: gridParams["cpc"]?.headerName || "CPC",
            field: "cpc",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPM",
            field: "cpm",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            minWidth: 140,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "CR",
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "CPA",
            field: "cpa",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },

            hide: true,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "ACoS",
            field: "acos",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            cellRendererParams: {
                hasPercent: true,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            headerName: "RoAS",
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            valueFormatter: numericValueFormatter,

            hide: true,
            cellRendererParams: {
                hasPercent: false,
                showChangePercent: true,
                hasPinnedColumnValue: true,
            },
        },
        {
            field: "match_type",
            headerName: "Match Type",
            valueFormatter: (params: ValueFormatterParams) =>
                titleCase(params.value),
            filter: "agTextColumnFilter",
            filterParams: {
                showTooltips: true,
            },
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "record_type",
            headerName: "Keyword Type",
            valueFormatter: FilterTextFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: FilterTextFormatter,
                values: ["keyword", "target"],
            },
            hide: true,
        },
        {
            field: "program_type",
            headerName: "Ad Type",
            filter: "agSetColumnFilter",
            cellClass: "text-center",
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? params.value.toUpperCase() : params.value,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
            minWidth: 100,
            hide: true,
        },
        {
            field: "campaign_name",
            headerName: "Campaign",
            filter: "agTextColumnFilter",
            cellRenderer: (params: IStatusBadgeCellRenderer) =>
                StatusBadgeCellRenderer(params, DeepDiveGridType.Keyword),
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "targetingtype",
            headerName: "Campaign Type",
            filter: "agSetColumnFilter",
            hide: true,
            maxWidth: 180,
            minWidth: 170,
            cellRenderer: ProgramTypeCellRenderer,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            filterParams: {
                valueFormatter: FilterTextFormatter,
            },
        },
    ];

    const keywordsColDefs = useGetModulesData({
        type: "columns",
        subGridType: SubGrids.KA_KEYWORDS,
        colDefs: productsModalColDefs,
        activeModuleName: ParentModule.PRODUCT_ANALYTICS,
        activeParentModuleName: ParentModule.PRODUCT_ANALYTICS,
    });

    return (
        <WidgetCard className="my-4" bodyPaddingClass=" p-0">
            <Row
                className="fs-5 fw-bold mb-3"
                justify="space-between"
                align="middle"
            >
                <Col className="ps-4 pt-4">
                    <h5>{title || "Keywords"}</h5>
                </Col>
            </Row>

            <Row className="d-flex w-100" style={{ height: "400px" }}>
                <div id="pa-keywords-grid" className="ag-theme-alpine w-100">
                    <AgGridModalLayout
                        {...{
                            rowData,
                            columnDefs: keywordsColDefs,
                            gridParams,
                            setGridApi,
                        }}
                        isRowSelectable={() => false}
                        isTotalRowsReq={true}
                    />
                </div>
            </Row>
        </WidgetCard>
    );
};

export default ProductKeywordsWidget;